import _ from "lodash";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import { store } from "store/createStore";
import { getItemLabel } from "Administrator/modules/Utils";
import { getAdminNode } from "Administrator/modules/AdministratorModule";
import {
  MODULE_CONFIGURATION_ID_MAP,
  FEED_MODE,
  FEED_TYPE,
  PERMISSION_STATUS_TYPE,
  FEED_DESIGNATION_ID_MAP,
} from "StaffPermissions/modules/constants";
import { getOrganizationCurriculumPrograms } from "StaffPermissions/modules/utils";

import { getStaffRoleAccessFeedQuery } from "StaffPermissions/modules/StaffPermissionsQueries";
import {
  updateAdminPermissionDetailsMutation,
  updateStaffModuleAccessMutation,
} from "StaffPermissions/modules/StaffPermissionsMutations";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getDefaultOrderBy } from "Utils/GetFormattedUserName";

export const MODULE_KEY = "staffPermissions";
// ----- redux actions -----
export const UPDATE_FILTERS = `UPDATE_FILTERS ${MODULE_KEY}`;
export const RESET_FILTERS = `RESET_FILTERS ${MODULE_KEY}`;
export const RESET_FEED_MODE = `RESET_FEED_MODE ${MODULE_KEY}`;
export const SET_FEED_MODE = `SET_FEED_MODE ${MODULE_KEY}`;
export const UPDATE_SORT_ORDER = `UPDATE_SORT_ORDER ${MODULE_KEY}`;
export const SET_LOADING = `SET_LOADING ${MODULE_KEY}`;
export const UPDATE_DIFF = `UPDATE_DIFF ${MODULE_KEY}`;
export const RESET_DIFF = `RESET_DIFF ${MODULE_KEY}`;

// also used in src/routes/routes/Administrator/modules/Utils.js
export const SET_DISCARD_DIALOG = `SET_DISCARD_DIALOG ${MODULE_KEY}`;

// ----- action creators -----
export const updateFilters = data => {
  return {
    data,
    type: UPDATE_FILTERS,
  };
};

export const resetFilters = data => {
  return {
    data,
    type: RESET_FILTERS,
  };
};

export const resetFeedMode = data => {
  return {
    data,
    type: RESET_FEED_MODE,
  };
};

export const setFeedMode = data => {
  return {
    data,
    type: SET_FEED_MODE,
  };
};

export const updateSortOrder = data => {
  return {
    data,
    type: UPDATE_SORT_ORDER,
  };
};

export const setLoading = data => {
  return {
    data,
    type: SET_LOADING,
  };
};

export const updateDiff = data => {
  return {
    data,
    type: UPDATE_DIFF,
  };
};

export const resetDiff = data => {
  return {
    data,
    type: RESET_DIFF,
  };
};

/**
 *
 * @param {object} data
 * @param {string=} data.curriculumProgramId
 * @param {string} data.feedKey
 * @param {function=} data.onDiscardConfirm
 */
export const setDiscardDialog = data => {
  return {
    data,
    type: SET_DISCARD_DIALOG,
  };
};

const isEquivalent = (a, b) => {
  if (_.isArray(a) && _.isArray(b)) {
    const setA = new Set(a);
    const setB = new Set(b);
    return _.isEqual(setA, setB);
  }

  return a === b;
};

export const getActiveFeedTitle = ({ t }) => {
  const state = store?.getState() ?? {};

  const { childId, subChildId } = _.get(state, "administrator.routes", {});

  const item = getAdminNode(subChildId || childId);
  const childRouteName = getItemLabel({
    t,
    item,
  });
  return childRouteName;
};

export const getFeedMode = ({ feedKey, curriculumProgramId }) => {
  const state = store?.getState() ?? {};

  if (feedKey === FEED_TYPE.TEACHERS) {
    return _.get(
      state,
      `${MODULE_KEY}.feedMode.${feedKey}.${curriculumProgramId}`,
      FEED_MODE.VIEW
    );
  }

  return _.get(state, `${MODULE_KEY}.feedMode.${feedKey}`);
};

export const getStaffRoleAccessFeedQueryVariables = ({
  feedKey,
  curriculumProgramId,
}) => {
  const state = store?.getState() ?? {};
  const academicYearIds = [getSelectedAcademicYearId(state)];

  const organizationId = _.get(state, "login.userInfo.org_id", "");

  return {
    organizationId: organizationId,
    filters: {
      designations: FEED_DESIGNATION_ID_MAP[feedKey],
      curriculumProgramIds: curriculumProgramId ? [curriculumProgramId] : [],
      archivalState: "ACTIVE",
      ...(academicYearIds ? { academicYearIds } : {}),
    },
    permissionCurriculumId: curriculumProgramId,
  };
};

// ----- redux thunks -----
export const getFeedDiff = ({ feedKey, curriculumProgramId }) => {
  const state = store?.getState() ?? {};

  if (feedKey === FEED_TYPE.TEACHERS) {
    return _.get(state, [MODULE_KEY, "diff", feedKey, curriculumProgramId], []);
  }
  return _.get(state, [MODULE_KEY, "diff", feedKey]);
};

export const getFeedMetadataWithUnsavedDiff = () => {
  const feedParams = [
    { feedKey: FEED_TYPE.ADMINISTRATORS },
    ..._.map(getOrganizationCurriculumPrograms(), program => ({
      feedKey: FEED_TYPE.TEACHERS,
      curriculumProgramId: program.id,
    })),
  ];
  const unSavedDiffFeeds = _.filter(
    feedParams,
    feedMetadata => !_.isEmpty(getFeedDiff(feedMetadata))
  );
  return unSavedDiffFeeds;
};

export const getRowWiseFeedDiff = ({ feedKey, curriculumProgramId }) => {
  const feedDiff = getFeedDiff({ feedKey, curriculumProgramId });
  const groupedChanges = _.groupBy(feedDiff, "id");
  const rowWiseFeedDiff = [];
  _.forEach(groupedChanges, rowChanges => {
    const combinedRowChanges = _.reduce(
      rowChanges,
      (acc, current) => {
        return { ...acc, ...current };
      },
      {}
    );
    rowWiseFeedDiff.push(combinedRowChanges);
  });
  return rowWiseFeedDiff;
};

const getUpdatedDiff = ({
  feedKey,
  curriculumProgramId,
  updatedValues,
  feedData,
}) => {
  const currentDiff = getFeedDiff({ feedKey, curriculumProgramId });
  const clonedCurrentDiff = _.clone(currentDiff);
  _.forEach(updatedValues, update => {
    const { id, fieldKey, fieldValue } = update;
    const diffIndex = _.findIndex(clonedCurrentDiff, item => {
      return id == item.id && _.has(item, fieldKey);
    });

    if (diffIndex === -1) {
      if (_.isEqual(fieldKey, "curriculumDefaultAccess")) {
        const feedItem = _.find(feedData, { id });
        if (isEquivalent(feedItem[fieldKey], fieldValue)) {
          return;
        }
      }
      clonedCurrentDiff.push({ id, [fieldKey]: fieldValue });
    } else {
      const feedItem = _.find(feedData, { id });
      // if the updated value is same as original feed data then delete the update from diff
      if (feedItem && isEquivalent(feedItem[fieldKey], fieldValue)) {
        clonedCurrentDiff.splice(diffIndex, 1);
      } else {
        // else mutate the diff to reflect latest value
        clonedCurrentDiff[diffIndex][fieldKey] = fieldValue;
      }
    }
  });
  return clonedCurrentDiff;
};

/**
 *
 * @param {Array<{id: string, curriculums: Array<Number>}>} updatedValues
 */
export const updateAdminPermissionDetails = (
  updatedValues,
  feedKey = FEED_TYPE.ADMINISTRATORS
) => {
  return async dispatch => {
    // no changes made
    if (_.isEmpty(updatedValues)) return true;

    let displayFieldEmpty = false;
    const mutationUpdateDataObject = _.transform(
      updatedValues,
      (result, updateValue) => {
        const {
          id: staffId,
          displayRole,
          curriculums: curriculumIds,
        } = updateValue;

        // check if display role not empty text
        if (displayRole === "") {
          displayFieldEmpty = true;
          return false;
        }

        result[staffId] = {
          staffId,
          ...result[staffId],
          ...(!_.isEmpty(displayRole) && { displayRole }),
          ...(!_.isEmpty(curriculumIds) && { curriculumIds }),
        };

        return true;
      },
      {}
    );

    if (displayFieldEmpty) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:please_fill_in_all_the_required_details",
        })
      );
      return false;
    }

    const mutationUpdateData = Object.values(mutationUpdateDataObject);
    try {
      dispatch(setLoading(true));
      await client.mutate({
        mutation: updateAdminPermissionDetailsMutation,
        variables: {
          updatedValues: mutationUpdateData,
        },
        refetchQueries: [
          {
            query: getStaffRoleAccessFeedQuery,
            variables: getStaffRoleAccessFeedQueryVariables({
              feedKey,
            }),
          },
        ],
        awaitRefetchQueries: true,
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:saved_successfully",
          type: "success",
        })
      );
      dispatch(setLoading(false));
      return true;
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      dispatch(setLoading(false));
      return false;
    }
  };
};

export const updateStaffModuleAccess = ({
  updatedValues,
  curriculumProgramId,
}) => {
  return async dispatch => {
    if (_.isEmpty(updatedValues)) {
      return true;
    }

    if (!curriculumProgramId) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return false;
    }

    let displayFieldEmpty = false;

    const mutationUpdatedValues = _.transform(
      updatedValues,
      (result, updatedStaffModuleAccess) => {
        const {
          id,
          displayRole,
          curriculumDefaultAccess: defaultAccess,
          ...modulesAccess
        } = updatedStaffModuleAccess;
        const configurations = [];
        _.forEach(modulesAccess, (value, key) => {
          configurations.push({
            id: MODULE_CONFIGURATION_ID_MAP[key],
            value: JSON.stringify(value),
          });
        });

        // check if display role not empty text
        if (displayRole === "") {
          displayFieldEmpty = true;
          return false;
        }
        result.push({
          staffId: id,
          curriculumProgramId,
          ...(!_.isEmpty(displayRole) && { displayRole }),
          ...(!_.isEmpty(defaultAccess) && { defaultAccess }),
          ...(!_.isEmpty(configurations) && { configurations }),
        });
        return true;
      }
    );

    if (displayFieldEmpty) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:please_fill_in_all_the_required_details",
        })
      );
      return false;
    }
    try {
      dispatch(setLoading(true));
      await client.mutate({
        mutation: updateStaffModuleAccessMutation,
        variables: {
          updatedValues: mutationUpdatedValues,
        },
        refetchQueries: [
          {
            query: getStaffRoleAccessFeedQuery,
            variables: getStaffRoleAccessFeedQueryVariables({
              feedKey: FEED_TYPE.TEACHERS,
              curriculumProgramId,
            }),
          },
        ],
        awaitRefetchQueries: true,
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:saved_successfully",
          type: "success",
        })
      );
      dispatch(setLoading(false));
      return true;
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      dispatch(setLoading(false));
      return false;
    }
  };
};

// ----- reducer -----
const REDUCER_HANDLERS = {
  [UPDATE_FILTERS]: (state, action) => {
    const { data } = action;
    return update(state, { filters: { $merge: data } });
  },
  [RESET_FILTERS]: state => {
    return update(state, { filters: { $set: initialState.filters } });
  },
  [RESET_FEED_MODE]: state => {
    return update(state, { feedMode: { $set: initialState.feedMode } });
  },
  [SET_FEED_MODE]: (state, action) => {
    const { data } = action;
    const { feedKey, mode, curriculumProgramId } = data;
    if (feedKey === FEED_TYPE.TEACHERS) {
      return update(state, {
        feedMode: {
          [FEED_TYPE.TEACHERS]: { [curriculumProgramId]: { $set: mode } },
        },
      });
    }
    return update(state, {
      feedMode: { [feedKey]: { $set: mode } },
    });
  },
  [UPDATE_SORT_ORDER]: (state, action) => {
    const { data } = action;
    const { id, sortDirection, menuItemEntity } = data;

    if (id === null) {
      const defaultSortOrder = getDefaultSortOrder();
      //resetting sorting
      return update(state, {
        sortFields: { $set: defaultSortOrder },
      });
    }

    const updatedSort = {
      [id]: { direction: sortDirection, menuItemEntity },
    };
    return update(state, {
      sortFields: {
        $set: updatedSort,
      },
    });
  },
  [SET_LOADING]: (state, action) => {
    const { data } = action;
    return update(state, { isLoading: { $set: data } });
  },
  [UPDATE_DIFF]: (state, action) => {
    const { data } = action;
    const { feedKey, curriculumProgramId, updatedValues, feedData } = data;
    const updatedDiff = getUpdatedDiff({
      feedKey,
      curriculumProgramId,
      updatedValues,
      feedData,
    });

    if (feedKey === FEED_TYPE.TEACHERS) {
      return update(state, {
        diff: {
          [FEED_TYPE.TEACHERS]: {
            [curriculumProgramId]: { $set: updatedDiff },
          },
        },
      });
    }

    return update(state, {
      diff: {
        [feedKey]: { $set: updatedDiff },
      },
    });
  },
  [RESET_DIFF]: (state, action) => {
    const { data } = action;
    if (_.isEmpty(data) || _.isUndefined(data)) {
      return update(state, { diff: { $set: initialState.diff } });
    }
    const { feedKey, curriculumProgramId } = data;
    if (feedKey === FEED_TYPE.TEACHERS) {
      return update(state, {
        diff: { [feedKey]: { [curriculumProgramId]: { $set: [] } } },
      });
    }
    return update(state, {
      diff: {
        [feedKey]: { $set: initialState.diff[feedKey] },
      },
    });
  },
  [SET_DISCARD_DIALOG]: (state, action) => {
    // also used in src/routes/routes/Administrator/modules/Utils.js (getStaffPermissionSideBarNodeOnLeaveInterceptor)
    const { data } = action;

    if (_.isEmpty(data) || _.isUndefined(data)) {
      return update(state, {
        discardDialog: { $set: initialState.discardDialog },
      });
    }

    return update(state, {
      discardDialog: {
        $set: data,
      },
    });
  },
};

export const getDefaultSortOrder = () => ({
  NAME: {
    menuItemEntity: getDefaultOrderBy({ type: "staff" }),
    direction: "ASC",
  },
});

const initialState = {
  filters: {
    searchText: "",
    permissions: PERMISSION_STATUS_TYPE.ALL,
    curriculumIds: [],
  },
  sortFields: {},
  feedMode: {
    [FEED_TYPE.ADMINISTRATORS]: FEED_MODE.VIEW,
    [FEED_TYPE.TEACHERS]: {},
  },
  diff: {
    [FEED_TYPE.ADMINISTRATORS]: [],
    [FEED_TYPE.TEACHERS]: {},
  },
  discardDialog: {},
  isLoading: false,
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
